import axios from 'axios';

function resolveApiBaseUrl() {
  if (typeof window !== 'undefined') {
    const host = (window.location.hostname || '').toLowerCase();
    if (host === 'agencialatamnews.com') {
      return 'https://www.agencialatamnews.com/api';
    }
  }

  const configured = (process.env.REACT_APP_API_URL || '').trim();
  if (!configured) return '/api';

  if (typeof window === 'undefined') {
    return configured;
  }

  try {
    const parsed = new URL(configured, window.location.origin);
    const apiIsLocalhost = ['localhost', '127.0.0.1'].includes(parsed.hostname);
    const browserIsLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (apiIsLocalhost && !browserIsLocalhost) {
      return '/api';
    }
    return configured;
  } catch {
    return '/api';
  }
}

const API_BASE_URL = resolveApiBaseUrl();
let refreshInFlight = null;
let refreshTimerId = null;

export async function emitSessionEvent(event, details = {}) {
  try {
    const token = localStorage.getItem('access_token');
    await fetch(`${API_BASE_URL}/auth/session-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ event, details }),
      keepalive: true,
    });
  } catch {
    // Intencionalmente silencioso para no afectar UX
  }
}

export function parseJwtPayload(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function parseBase64JsonToken(token) {
  try {
    return JSON.parse(atob(token));
  } catch {
    return null;
  }
}

export function getAccessTokenPayload(token) {
  if (!token) return null;
  return token.includes('.') ? parseJwtPayload(token) : parseBase64JsonToken(token);
}

export function getAccessTokenExpiryMs(token) {
  const payload = getAccessTokenPayload(token);
  const expSeconds = Number(payload?.exp || 0);
  if (!Number.isFinite(expSeconds) || expSeconds <= 0) return null;
  return expSeconds * 1000;
}

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

export function clearAuthStorage() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
}

export function buildUserFromPayload(payload, fallbackUser = null) {
  if (!payload && !fallbackUser) return null;

  const normalized = {
    id: payload?.user_id ?? payload?.id ?? fallbackUser?.id ?? null,
    username: payload?.username ?? fallbackUser?.username ?? null,
    email: payload?.email ?? fallbackUser?.email ?? null,
    role: payload?.role ?? fallbackUser?.role ?? null,
  };

  if (!normalized.id && !normalized.username && !normalized.email && !normalized.role) {
    return null;
  }

  return normalized;
}

export function hydrateUserFromAccessToken() {
  const token = localStorage.getItem('access_token');
  if (!token) return null;

  const payload = getAccessTokenPayload(token);
  const previousUser = getStoredUser();
  const merged = buildUserFromPayload(payload, previousUser);

  if (merged) {
    localStorage.setItem('user', JSON.stringify(merged));
  }

  return merged;
}

export function getCurrentUser() {
  const stored = getStoredUser();
  if (stored?.role) return stored;
  return hydrateUserFromAccessToken();
}

export async function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        await emitSessionEvent('refresh_failed', { reason: 'missing_refresh_token' });
        throw new Error('Missing refresh token');
      }

      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refresh_token: refreshToken },
        { timeout: 30000 }
      );

      const newAccessToken = response?.data?.access_token;
      if (!newAccessToken) {
        await emitSessionEvent('refresh_failed', { reason: 'invalid_refresh_response' });
        throw new Error('Invalid refresh response');
      }

      localStorage.setItem('access_token', newAccessToken);
      hydrateUserFromAccessToken();
      await emitSessionEvent('refresh_success');
      return newAccessToken;
    })().catch(async (error) => {
      await emitSessionEvent('refresh_failed', { reason: error?.message || 'unknown' });
      throw error;
    }).finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

export function stopSessionRefreshLoop() {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
}

export function startSessionRefreshLoop(options = {}) {
  const { onSessionExpired } = options;
  stopSessionRefreshLoop();

  const scheduleNext = () => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) return;

    const expAt = getAccessTokenExpiryMs(accessToken);
    if (!expAt) return;

    const now = Date.now();
    const refreshLeadMs = 60 * 1000;
    const delayMs = Math.max(expAt - now - refreshLeadMs, 5000);

    refreshTimerId = setTimeout(async () => {
      try {
        await refreshAccessToken();
        scheduleNext();
      } catch {
        await emitSessionEvent('session_expired');
        clearAuthStorage();
        if (typeof onSessionExpired === 'function') {
          onSessionExpired();
        } else {
          redirectToLoginPreservingNext('session_expired');
        }
      }
    }, delayMs);
  };

  scheduleNext();
  return stopSessionRefreshLoop;
}

export function redirectToLoginPreservingNext(reason = 'auth') {
  if (typeof window === 'undefined') return;
  const next = `${window.location.pathname}${window.location.search || ''}`;
  const target = `/login?next=${encodeURIComponent(next)}&reason=${encodeURIComponent(reason)}`;
  if (window.location.pathname !== '/login') {
    window.location.assign(target);
  }
}
